import type { Pairing } from '@/common/db-typebox';
import type { MenuWithPairing } from '@/common/domain-model';
import {
  CheckSquareOutlined,
  CloseSquareOutlined,
  GlobalOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Input,
  Space,
  Spin,
  Tabs,
  Tooltip,
} from 'antd';
import clsx from 'clsx';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { usePairingStore } from '../store/PairingContext';

const highlightText = (text: string, search: string) => {
  if (!search) return text;
  const regex = new RegExp(`(${search})`, 'gi');
  return text.split(regex).map((part, i) =>
    regex.test(part) ? (
      <span
        key={`${part}-${String(i)}`}
        className="bg-yellow-200 font-black px-1"
      >
        {part}
      </span>
    ) : (
      part
    ),
  );
};

interface ItemCardProps {
  title: string;
  description?: string;
  status?: string;
  isPublic?: boolean;
  isSelected: boolean;
  onClick: () => void;
  searchText: string;
  createdAt?: Date;
  isSelectionMode?: boolean;
  isChecked?: boolean;
  onCheck?: (checked: boolean) => void;
}

const ItemCard = ({
  title,
  description,
  status,
  isPublic,
  isSelected,
  onClick,
  searchText,
  createdAt,
  isSelectionMode,
  isChecked,
  onCheck,
}: ItemCardProps) => {
  const showCheckbox = isSelectionMode && (status || isPublic);

  return (
    <Card
      size="small"
      hoverable
      className={`cursor-pointer ${
        isSelected ? 'border-blue-500 hover:border-blue-500 border-2' : ''
      }`}
      onClick={() => {
        if (!isSelectionMode) {
          onClick();
        }
      }}
    >
      <div className="flex items-start gap-2">
        {showCheckbox && (
          <Checkbox
            checked={isChecked}
            onChange={(e) => onCheck?.(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <div className="flex-1">
          <div className="font-medium line-clamp-2">
            {highlightText(title, searchText)}
          </div>
          {description && (
            <div className="text-sm text-gray-500 line-clamp-2">
              {highlightText(description, searchText)}
            </div>
          )}
          <div className="flex flex-col justify-start items-start gap-2">
            {createdAt && (
              <div className="text-sm text-gray-500 text-[12px]">
                {format(createdAt, 'yyyy-MM-dd HH:mm')}
              </div>
            )}
            {(status || isPublic) && (
              <div className="flex justify-start items-center gap-2">
                {status && (
                  <div
                    className={clsx('text-xs px-2 py-0.5 rounded-full', {
                      'bg-yellow-100 text-yellow-800': status === 'pending',
                      'bg-blue-100 text-blue-800': status === 'processing',
                      'bg-green-100 text-green-800': status === 'completed',
                      'bg-red-100 text-red-800': status === 'failed',
                    })}
                  >
                    {status}
                  </div>
                )}
                {isPublic && (
                  <Tooltip title="Public">
                    <GlobalOutlined className="text-blue-500" />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export const HistorySidebar = observer(() => {
  const [searchHistory, setSearchHistory] = useState<string>('');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const pairingStore = usePairingStore();

  const filteredHistories = pairingStore.pairingHistories.filter((history) =>
    history.params.dish_description
      .toLowerCase()
      .includes(searchHistory.toLowerCase()),
  );

  const filteredMenus = pairingStore.menus.filter((menu) => {
    const search = searchHistory.toLowerCase();
    return (
      menu.name.toLowerCase().includes(search) ||
      menu.description?.toLowerCase().includes(search) ||
      menu.pairing?.params.dish_description.toLowerCase().includes(search)
    );
  });

  const handleSelectAll = () => {
    const items =
      pairingStore.activeTab === 'history' ? filteredHistories : filteredMenus;
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(
        new Set(
          items
            .map((item) =>
              pairingStore.activeTab === 'history'
                ? (item as Pairing).id
                : (item as MenuWithPairing).pairing?.id || '',
            )
            .filter(Boolean),
        ),
      );
    }
  };

  const handleTogglePublic = async () => {
    if (selectedItems.size === 0) return;
    await pairingStore.togglePublic(Array.from(selectedItems));
    await pairingStore.fetchPairingData();
    setSelectedItems(new Set());
    setIsSelectionMode(false);
  };

  return (
    <div className="w-72 flex flex-col h-full overflow-hidden my-[24px]">
      <div className="sticky top-0 z-10 bg-white mx-[24px]">
        <Space direction="vertical" className="w-full">
          <div className="flex gap-2">
            <Input
              placeholder="Search history..."
              onPressEnter={(e) => {
                console.log('onPressEnter', e);
                setSearchHistory((e.target as HTMLInputElement).value);
              }}
              onClear={() => {
                setSearchHistory('');
              }}
              allowClear
              className="flex-1"
              prefix={<SearchOutlined />}
            />
            <Tooltip title="Refresh list">
              <Button
                icon={<ReloadOutlined />}
                onClick={async () => {
                  pairingStore.handleSelect(undefined);
                  await pairingStore.fetchPairingData((data) => {
                    if (!pairingStore.currentSelection) {
                      pairingStore.handleSelect(undefined);
                      return;
                    }

                    const item = data.find((item) =>
                      pairingStore.currentSelection?.type === 'pairing'
                        ? item.id === pairingStore.currentSelection.pairing.id
                        : (item as MenuWithPairing).pairing?.id ===
                          pairingStore.currentSelection?.menu.pairing?.id,
                    );

                    if (item && pairingStore.currentSelection) {
                      pairingStore.handleSelect(
                        pairingStore.currentSelection.type === 'pairing'
                          ? {
                              type: 'pairing',
                              pairing: item as Pairing,
                            }
                          : {
                              type: 'menu',
                              menu: item as MenuWithPairing,
                            },
                      );
                    }
                  });
                }}
                loading={pairingStore.isLoading}
              />
            </Tooltip>
            <Tooltip
              title={
                isSelectionMode ? 'Exit selection mode' : 'Enter selection mode'
              }
            >
              <Button
                icon={
                  isSelectionMode ? (
                    <CloseSquareOutlined />
                  ) : (
                    <CheckSquareOutlined />
                  )
                }
                onClick={() => {
                  setIsSelectionMode(!isSelectionMode);
                  setSelectedItems(new Set());
                }}
                type={isSelectionMode ? 'primary' : 'default'}
              />
            </Tooltip>
          </div>
          <Tabs
            activeKey={pairingStore.activeTab}
            onChange={async (key) => {
              pairingStore.setActiveTab(key as 'history' | 'menu');
              await pairingStore.fetchPairingData();
              setSelectedItems(new Set());
            }}
            items={[
              {
                key: 'history',
                label: 'History',
              },
              {
                key: 'menu',
                label: 'Menu',
              },
            ]}
            className="w-full"
          />
        </Space>
      </div>
      <div
        className={clsx('space-y-2 overflow-y-auto flex-1 px-[24px]', {
          'mb-[48px]': pairingStore.activeTab === 'menu' || isSelectionMode,
        })}
      >
        {pairingStore.isLoading ? (
          <div className="flex justify-center">
            <Spin />
          </div>
        ) : (
          <>
            {pairingStore.activeTab === 'history' &&
              filteredHistories.map((history) => (
                <ItemCard
                  key={history.id}
                  title={history.params.dish_description}
                  status={history.status}
                  isPublic={history.public}
                  isSelected={
                    pairingStore.currentSelection?.type === 'pairing' &&
                    pairingStore.currentSelection.pairing.id === history.id
                  }
                  onClick={() =>
                    pairingStore.handleSelect({
                      type: 'pairing',
                      pairing: history,
                    })
                  }
                  searchText={searchHistory}
                  createdAt={new Date(history.created_at)}
                  isSelectionMode={isSelectionMode}
                  isChecked={selectedItems.has(history.id)}
                  onCheck={(checked) => {
                    const newSelected = new Set(selectedItems);
                    if (checked) {
                      newSelected.add(history.id);
                    } else {
                      newSelected.delete(history.id);
                    }
                    setSelectedItems(newSelected);
                  }}
                />
              ))}
            {pairingStore.activeTab === 'menu' &&
              filteredMenus.map((menu) => (
                <ItemCard
                  key={menu.id}
                  title={menu.name}
                  description={menu.description || undefined}
                  status={menu.pairing?.status}
                  isPublic={menu.pairing?.public}
                  isSelected={
                    pairingStore.currentSelection?.type === 'menu' &&
                    pairingStore.currentSelection.menu.id === menu.id
                  }
                  onClick={() =>
                    pairingStore.handleSelect({ type: 'menu', menu })
                  }
                  searchText={searchHistory}
                  isSelectionMode={isSelectionMode}
                  isChecked={
                    menu.pairing?.id
                      ? selectedItems.has(menu.pairing.id)
                      : false
                  }
                  onCheck={(checked) => {
                    if (!menu.pairing?.id) return;
                    const newSelected = new Set(selectedItems);
                    if (checked) {
                      newSelected.add(menu.pairing.id);
                    } else {
                      newSelected.delete(menu.pairing.id);
                    }
                    setSelectedItems(newSelected);
                  }}
                />
              ))}
          </>
        )}
      </div>
      {isSelectionMode ? (
        <div className="flex justify-center gap-2 p-[24px] h-[100px]">
          <Tooltip
            title={
              selectedItems.size ===
              (pairingStore.activeTab === 'history'
                ? filteredHistories.length
                : filteredMenus.length)
                ? 'Deselect all items'
                : 'Select all items'
            }
          >
            <Button onClick={handleSelectAll}>
              {selectedItems.size ===
              (pairingStore.activeTab === 'history'
                ? filteredHistories.length
                : filteredMenus.length)
                ? 'Deselect All'
                : 'Select All'}
            </Button>
          </Tooltip>
          <Tooltip
            title={
              selectedItems.size === 0
                ? 'Please select at least one item'
                : 'Toggle public/private status for selected items'
            }
          >
            <Button
              type="primary"
              onClick={handleTogglePublic}
              disabled={selectedItems.size === 0}
            >
              Toggle Public
            </Button>
          </Tooltip>
        </div>
      ) : pairingStore.activeTab === 'history' ? (
        <div className="flex justify-center p-[24px] h-[100px]">
          <Tooltip title="Create a new pairing">
            <Button
              type="primary"
              block
              onClick={pairingStore.handleAddPairing}
            >
              New Pairing
            </Button>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
});
